import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Paper,
  Grid,
  LinearProgress,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  TrendingUp as TrendingUpIcon,
  LocalFireDepartment as CalorieIcon,
  FitnessCenter as ProteinIcon,
  Grain as CarbIcon,
  Opacity as FatIcon,
  Timeline as TimelineIcon,
  CalendarToday as CalendarIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { calorieAIService } from '../../services/calorieAI/calorieAIService';

const ProgressTracking = ({ onBack }) => {
  const [todayData, setTodayData] = useState({
    consumed_calories: 0,
    consumed_protein: 0,
    consumed_carbs: 0,
    consumed_fat: 0,
    meal_count: 0
  });
  
  const [goals, setGoals] = useState({
    daily_calories: 2000,
    daily_protein: 150,
    daily_carbs: 250,
    daily_fat: 67
  });

  const [weeklyData, setWeeklyData] = useState([]);
  const [showWeightDialog, setShowWeightDialog] = useState(false);
  const [newWeight, setNewWeight] = useState('');
  const [weightHistory, setWeightHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadProgressData();
  }, []);

  const loadProgressData = async () => {
    try {
      setLoading(true);
      const [todayStats, currentGoals, weekStats, weights] = await Promise.all([
        calorieAIService.getTodayStats(),
        calorieAIService.getActiveGoals(),
        calorieAIService.getWeeklyStats(),
        calorieAIService.getWeightHistory()
      ]);

      setTodayData(todayStats);
      setGoals(currentGoals || {
        daily_calories: 2000,
        daily_protein: 50,
        daily_carbs: 250,
        daily_fat: 65
      });
      setWeeklyData(weekStats);
      setWeightHistory(weights);
    } catch (error) {
      console.error('載入進度數據失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddWeight = async () => {
    try {
      const weight = parseFloat(newWeight);
      if (!weight || weight < 30 || weight > 200) return;

      await calorieAIService.addWeightRecord(weight);
      const updatedWeights = await calorieAIService.getWeightHistory();
      setWeightHistory(updatedWeights);
      
      setNewWeight('');
      setShowWeightDialog(false);
    } catch (error) {
      console.error('添加體重記錄失敗:', error);
    }
  };

  const getProgressPercentage = (consumed, goal) => {
    return Math.min((consumed / goal) * 100, 100);
  };

  const getProgressColor = (percentage) => {
    if (percentage < 50) return '#FF6B6B';
    if (percentage < 80) return '#FF9800';
    if (percentage < 100) return '#4CAF50';
    return '#2196F3';
  };

  const getCalorieStatus = () => {
    const percentage = getProgressPercentage(todayData.consumed_calories, goals?.daily_calories || 2000);
    if (percentage < 70) return { text: '需要增加攝取', color: '#FF6B6B' };
    if (percentage < 90) return { text: '接近目標', color: '#FF9800' };
    if (percentage <= 110) return { text: '達成目標', color: '#4CAF50' };
    return { text: '超過目標', color: '#F44336' };
  };

  const renderTodayProgress = () => (
    <Card sx={{ mb: 3, borderRadius: '16px' }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
          <TrendingUpIcon sx={{ mr: 1, color: '#FF6B6B' }} />
          今日進度
        </Typography>

        {/* 卡路里進度 */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#333' }}>
              {Math.round(todayData.consumed_calories)}
            </Typography>
            <Typography variant="h6" color="textSecondary">
              / {goals?.daily_calories || 2000} 卡
            </Typography>
          </Box>
          
          <LinearProgress
            variant="determinate"
            value={getProgressPercentage(todayData.consumed_calories, goals?.daily_calories || 2000)}
            sx={{
              height: 12,
              borderRadius: 6,
              backgroundColor: '#f0f0f0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: getProgressColor(getProgressPercentage(todayData.consumed_calories, goals?.daily_calories || 2000)),
                borderRadius: 6
              }
            }}
          />
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
            <Chip 
              label={getCalorieStatus().text}
              sx={{ 
                backgroundColor: getCalorieStatus().color,
                color: 'white',
                fontWeight: 'bold'
              }}
            />
            <Typography variant="body2" color="textSecondary">
              剩餘 {Math.max(0, Math.round(goals.daily_calories - todayData.consumed_calories))} 卡
            </Typography>
          </Box>
        </Box>

        {/* 巨量營養素進度 */}
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Box sx={{ textAlign: 'center' }}>
              <ProteinIcon sx={{ fontSize: 32, color: '#4CAF50', mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {todayData.consumed_protein}g
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                / {goals?.daily_protein || 50}g
              </Typography>
              <LinearProgress
                variant="determinate"
                value={getProgressPercentage(todayData.consumed_protein, goals?.daily_protein || 50)}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: '#E8F5E8',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#4CAF50',
                    borderRadius: 3
                  }
                }}
              />
              <Typography variant="caption" color="textSecondary">
                蛋白質
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={4}>
            <Box sx={{ textAlign: 'center' }}>
              <CarbIcon sx={{ fontSize: 32, color: '#2196F3', mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {todayData.consumed_carbs}g
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                / {goals?.daily_carbs || 250}g
              </Typography>
              <LinearProgress
                variant="determinate"
                value={getProgressPercentage(todayData.consumed_carbs, goals?.daily_carbs || 250)}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: '#E3F2FD',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#2196F3',
                    borderRadius: 3
                  }
                }}
              />
              <Typography variant="caption" color="textSecondary">
                碳水化合物
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={4}>
            <Box sx={{ textAlign: 'center' }}>
              <FatIcon sx={{ fontSize: 32, color: '#FF9800', mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {todayData.consumed_fat}g
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                / {goals?.daily_fat || 65}g
              </Typography>
              <LinearProgress
                variant="determinate"
                value={getProgressPercentage(todayData.consumed_fat, goals?.daily_fat || 65)}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: '#FFF3E0',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#FF9800',
                    borderRadius: 3
                  }
                }}
              />
              <Typography variant="caption" color="textSecondary">
                脂肪
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* 今日統計 */}
        <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">
                今日餐數
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {todayData.meal_count} 餐
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="textSecondary">
                平均每餐
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {todayData.meal_count > 0 ? Math.round(todayData.consumed_calories / todayData.meal_count) : 0} 卡
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );

  const renderWeeklyTrend = () => (
    <Card sx={{ mb: 3, borderRadius: '16px' }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
          <TimelineIcon sx={{ mr: 1, color: '#FF6B6B' }} />
          本週趨勢
        </Typography>

        <Grid container spacing={1}>
          {weeklyData.map((day, index) => {
            const dayNames = ['週日', '週一', '週二', '週三', '週四', '週五', '週六'];
            const percentage = getProgressPercentage(day.calories, goals.daily_calories);
            
            return (
              <Grid item xs key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="textSecondary">
                    {dayNames[index]}
                  </Typography>
                  <Box
                    sx={{
                      height: 60,
                      width: '100%',
                      backgroundColor: '#f0f0f0',
                      borderRadius: '4px',
                      position: 'relative',
                      my: 1
                    }}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: `${Math.min(percentage, 100)}%`,
                        backgroundColor: getProgressColor(percentage),
                        borderRadius: '4px',
                        transition: 'height 0.3s ease'
                      }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ fontSize: '10px' }}>
                    {Math.round(day.calories)}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>

        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            週平均: {Math.round(weeklyData.reduce((sum, day) => sum + day.calories, 0) / 7)} 卡/天
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  const renderWeightTracking = () => (
    <Card sx={{ mb: 3, borderRadius: '16px' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
            <CalendarIcon sx={{ mr: 1, color: '#FF6B6B' }} />
            體重追蹤
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setShowWeightDialog(true)}
            sx={{ borderRadius: '20px' }}
          >
            記錄
          </Button>
        </Box>

        {weightHistory.length > 0 ? (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#333' }}>
                {weightHistory[weightHistory.length - 1].weight} kg
              </Typography>
              <Typography variant="body2" color="textSecondary">
                最新記錄
              </Typography>
            </Box>

            {weightHistory.length > 1 && (
              <Box sx={{ mb: 2 }}>
                {(() => {
                  const latest = weightHistory[weightHistory.length - 1].weight;
                  const previous = weightHistory[weightHistory.length - 2].weight;
                  const change = latest - previous;
                  const isIncrease = change > 0;
                  
                  return (
                    <Chip
                      label={`${isIncrease ? '+' : ''}${change.toFixed(1)} kg`}
                      sx={{
                        backgroundColor: isIncrease ? '#FFE0E0' : '#E8F5E8',
                        color: isIncrease ? '#FF6B6B' : '#4CAF50',
                        fontWeight: 'bold'
                      }}
                    />
                  );
                })()}
              </Box>
            )}

            {/* 簡單的體重趨勢圖 */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                最近記錄:
              </Typography>
              {weightHistory.slice(-5).reverse().map((record, index) => (
                <Box key={record.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 1 }}>
                  <Typography variant="body2">
                    {new Date(record.recorded_at).toLocaleDateString('zh-TW')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {record.weight} kg
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="textSecondary">
              還沒有體重記錄，點擊上方按鈕開始記錄
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ minHeight: '100vh' }}>
      {/* 頂部導航 */}
      <Paper sx={{ 
        p: 2, 
        display: 'flex', 
        alignItems: 'center',
        backgroundColor: 'white',
        borderRadius: 0,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <IconButton onClick={onBack} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          進度追蹤
        </Typography>
      </Paper>

      {/* 內容區域 */}
      <Box sx={{ p: 2, pb: 10 }}>
        {renderTodayProgress()}
        {renderWeeklyTrend()}
        {renderWeightTracking()}
      </Box>

      {/* 添加體重記錄對話框 */}
      <Dialog 
        open={showWeightDialog} 
        onClose={() => setShowWeightDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>記錄體重</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="體重 (kg)"
            type="number"
            value={newWeight}
            onChange={(e) => setNewWeight(e.target.value)}
            inputProps={{ min: 30, max: 200, step: 0.1 }}
            sx={{ mt: 2 }}
            autoFocus
          />
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            記錄日期: {new Date().toLocaleDateString('zh-TW')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowWeightDialog(false)}>取消</Button>
          <Button 
            onClick={handleAddWeight}
            variant="contained"
            disabled={!newWeight}
            sx={{ background: 'linear-gradient(135deg, #4CAF50, #66BB6A)' }}
          >
            記錄
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProgressTracking;